import React, { useRef } from "react";
import img from "../../../constant";
import { X } from "lucide-react";
import formatText from "../../../utility/formatText";

interface SlaContentProps {
  clientName: string;
  representativeName: string;
  representativeRole: string;
  date: string;
  uploadedSignature?: File | null;
  signaturePreview?: string | null;
  onFileUpload?: (file: File) => void;
  onRemoveSignature?: () => void;
  readOnly?: boolean;
}

const SlaContent: React.FC<SlaContentProps> = ({
  clientName,
  date,
  representativeName,
  representativeRole,
  uploadedSignature,
  signaturePreview,
  onFileUpload,
  onRemoveSignature,
  readOnly = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        alert("Please upload a valid image file (JPG, JPEG, or PNG)");
        return;
      }
      onFileUpload?.(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveSignature?.();
  };
  return (
    <div className="font-abel text-gray-700 text-sm leading-relaxed space-y-6 p-5 bg-white shadow-sm rounded-lg border border-gray-100 max-w-[1024px] mx-auto">
      <div className="text-center mb-10 border-b pb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">25th Suite</h1>
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">
          Service Level Agreement
        </h2>
        <p className="text-lg">for</p>
        <p className="text-xl font-bold text-primary my-2">{clientName}</p>
        <br />
        {/* <p className="text-md text-gray-500">Prepared by 25th & Staffing</p> */}
        <p className="text-md text-gray-500 mt-2">Date: {date}</p>
      </div>

      <div className="bg-gray-50 p-6 rounded-md mb-8">
        <h3 className="font-bold text-lg mb-4">Table of Contents</h3>
        <ul className="list-decimal pl-5 space-y-1">
          <li>Purpose and Scope</li>
          <li>Uptime and Availability Commitments</li>
          <li>Support and Incident Response</li>
          <li>Security and Compliance</li>
          <li>Disaster Recovery and Business Continuity</li>
          <li>Responsibilities</li>
          <li>Signatures</li>
          {/* <li>{clientName} Responsibility</li>
          <li>25th Direct Responsibilities</li>
          <li>ANNEX B – Data Processing Addendum (DPA)</li>
          <li>Support Handbook (Annex C)</li>
          <li>Implementation Timeline & Express Mode</li>
          <li>General Provisions</li> */}
        </ul>
      </div>

      <section>
        <h3 className="font-bold text-lg mb-2">1. Purpose and Scope</h3>
        <p>
          This SLA forms Exhibit A to the Master Services Agreement. 
          It defines Provider's service performance commitments, security standards, 
          support obligations, and operational responsibilities for the full duration 
          of the subscription term as stated in the applicable Subscription Order.
        </p>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">
          2. Uptime and Availability Commitments
        </h3>
          <p>
            Provider commits to the following monthly uptime targets, excluding 
            scheduled maintenance windows and excused downtime:
          </p>
          <div className="overflow-x-auto mt-2">
            <table className="min-w-full text-left text-xs border">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  <th className="p-2 border">Tier</th>
                  <th className="p-2 border">Uptime SLA</th>
                  <th className="p-2 border">Max Downtime / Month</th>
                  <th className="p-2 border">Applies To</th>
                  <th className="p-2 border">Credit Trigger</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-semibold">
                    Starter
                  </td>
                  <td className="p-2 border">
                    Best Effort
                  </td>
                  <td className="p-2 border font-semibold">
                    No Commitment
                  </td>
                  <td className="p-2 border font-semibold">
                    25th Direct & AI Recruiter & Console — Starter
                  </td>
                  <td className="p-2 border font-semibold">
                    No Credits
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Growth
                  </td>
                  <td className="p-2 border">
                    99.5%
                  </td>
                  <td className="p-2 border font-semibold">
                    ~3.6 hours
                  </td>
                  <td className="p-2 border font-semibold">
                    25th Direct & AI Recruiter & Console — Growth
                  </td>
                  <td className="p-2 border font-semibold">
                    Below 99.5%
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Scale
                  </td>
                  <td className="p-2 border">
                    99.9%
                  </td>
                  <td className="p-2 border font-semibold">
                    ~44 minutes
                  </td>
                  <td className="p-2 border font-semibold">
                    25th Direct & AI Recruiter & Console — Scale
                  </td>
                  <td className="p-2 border font-semibold">
                    Below 99.9%
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Enterprise
                  </td>
                  <td className="p-2 border">
                    99.99%
                  </td>
                  <td className="p-2 border font-semibold">
                    ~4 minutes
                  </td>
                  <td className="p-2 border font-semibold">
                    All products — Enterprise
                  </td>
                  <td className="p-2 border font-semibold">
                    Below 99.99%
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Lifecycle (Managed)
                  </td>
                  <td className="p-2 border">
                    99.9%
                  </td>
                  <td className="p-2 border font-semibold">
                    ~44 minutes
                  </td>
                  <td className="p-2 border font-semibold">
                    Employee Lifecycle — all managed tiers
                  </td>
                  <td className="p-2 border font-semibold">
                    Below 99.9%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-5">
            <strong>Scheduled Maintenance Window:</strong> Sundays 00:00–04:00 WAT. 
            Provider shall notify Customer at least 48 hours in advance. Scheduled maintenance 
            windows do not count toward uptime calculations. Emergency maintenance may be performed 
            outside this window with as much prior notice as practicable.
          </p>
          
        

        <div className="mb-4">
          <p className="font-bold text-lg mb-2 mt-3">
            <strong>3. Support and Incident Response</strong>
          </p>
          <div className="overflow-x-auto mt-2">
            <table className="min-w-full text-left text-xs border">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  <th className="p-2 border">Support Metric</th>
                  <th className="p-2 border">Starter</th>
                  <th className="p-2 border">Growth</th>
                  <th className="p-2 border">Scale</th>
                  <th className="p-2 border">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-semibold">
                    Primary Channel
                  </td>
                  <td className="p-2 border">
                    Email
                  </td>
                  <td className="p-2 border font-semibold">
                    Email & Live Chat
                  </td>
                  <td className="p-2 border font-semibold">
                    Telephone 24/5
                  </td>
                  <td className="p-2 border font-semibold">
                    Dedicated CSM
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Response SLA
                  </td>
                  <td className="p-2 border">
                    48 hours
                  </td>
                  <td className="p-2 border font-semibold">
                    8 hours
                  </td>
                  <td className="p-2 border font-semibold">
                    4 hours
                  </td>
                  <td className="p-2 border font-semibold">
                    2 hours
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Critical Incident
                  </td>
                  <td className="p-2 border">
                    Best effort
                  </td>
                  <td className="p-2 border font-semibold">
                    4-hour response
                  </td>
                  <td className="p-2 border font-semibold">
                    2-hour response
                  </td>
                  <td className="p-2 border font-semibold">
                    1-hour — named contact
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Uptime Monitoring
                  </td>
                  <td className="p-2 border">
                    Standard
                  </td>
                  <td className="p-2 border font-semibold">
                    Standard
                  </td>
                  <td className="p-2 border font-semibold">
                    Enhanced
                  </td>
                  <td className="p-2 border font-semibold">
                    Real-time dashboard
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Business Reviews
                  </td>
                  <td className="p-2 border">
                    —
                  </td>
                  <td className="p-2 border font-semibold">
                    —
                  </td>
                  <td className="p-2 border font-semibold">
                    Quarterly
                  </td>
                  <td className="p-2 border font-semibold">
                    Monthly
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-4">
          <p className="font-bold text-lg mb-2">
            4.  Security and Compliance
          </p>
          <p className="mt-2">Provider shall maintain the following minimum security controls throughout the subscription term:</p>

          <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
              	Encryption: All data at rest encrypted using AES-256; all data in transit encrypted using TLS 1.3 or higher
              </li>
              <li>
                Access Controls: Role-based access control (RBAC), multi-factor authentication (MFA), and IP whitelisting where applicable
              </li>
              <li>
              	Monitoring: Continuous security event logging, intrusion detection systems, and annual third-party penetration testing
              </li>
              <li>
              	Physical Security: Services hosted in ISO 27001-compliant data centres via AWS
              </li>
              <li>
              	Incident Response: 24/7 security monitoring; breach notification within 72 hours per DPA Section 3.5; documented and tested incident response plan
              </li>
            </ul>
        </div>


        <div className="mb-4">
          <h4 className="font-bold text-lg mb-2">
            5.  Disaster Recovery and Business Continuity
          </h4>
          <div className="overflow-x-auto mt-2">
            <table className="min-w-full text-left text-xs border">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  <th className="p-2 border">DR Metric</th>
                  <th className="p-2 border">Starter</th>
                  <th className="p-2 border">Growth / Scale</th>
                  <th className="p-2 border">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-semibold">
                    Recovery Time Objective (RTO)
                  </td>
                  <td className="p-2 border">
                    8 hours
                  </td>
                  <td className="p-2 border font-semibold">
                    4 hours / 1 hour
                  </td>
                  <td className="p-2 border font-semibold">
                    1 hour
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Recovery Point Objective (RPO)
                  </td>
                  <td className="p-2 border">
                    24 hours
                  </td>
                  <td className="p-2 border font-semibold">
                    4 hours / 1 hour
                  </td>
                  <td className="p-2 border font-semibold">
                    15 minutes
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Backup Retention
                  </td>
                  <td className="p-2 border">
                    30 days
                  </td>
                  <td className="p-2 border font-semibold">
                    90 days
                  </td>
                  <td className="p-2 border font-semibold">
                    365 days
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">
          6.  Responsibilities
        </h3>
        <p>Customer agrees to: provide timely access to systems, data, 
          and personnel required for implementation and support; designate a 
          named point of contact for go-live confirmation and escalation; respond 
          to Provider queries within 3–5 business days; and ensure Authorised Users 
          comply with Provider's acceptable use requirements.
        </p>
        <p className="mt-5">Provider agrees to: deliver the Services materially as described in the 
          applicable Subscription Order and this SLA; maintain the infrastructure and 
          security controls set out herein; provide advance notice of planned maintenance 
          and material platform changes; and deliver support as per Section 3.
        </p>
        
      </section>

      {/* <section>
        <h3 className="font-bold text-lg mb-2">
          5. Support & Incident Response
        </h3>
        <p>
          <strong>5.1 Response Targets:</strong> S1-Critical (1 hr), S2-High (4
          hrs), S3-Medium (1 day), S4-Low (2 days).
        </p>
        <p className="mt-2">
          <strong>5.2 Authorized Support Contacts:</strong> Support tickets
          accepted only from designated Administrators (IT/HR).
        </p>
        <p className="mt-2">
          <strong>5.3 Storage Fair Use Policy:</strong> Enterprise Plan includes
          100 GB. Overage billed at prevailing rate.
        </p>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">6. Security & Compliance</h3>
        <p>
          AES-256 encryption at rest, TLS 1.2+ in transit, NDPA 2023 compliance.
        </p>
        <p className="mt-1">
          <strong>6.1 Data Privacy & Integrity:</strong> Strict prohibition on
          data sale or misuse.
        </p>
        <p className="mt-1">
          <strong>6.2 IP & Account Sharing:</strong> No sharing credentials with
          third parties without consent.
        </p>
        <p className="mt-1">
          <strong>6.3 Audit Rights:</strong> Quarterly audit of usage logs.
        </p>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">
          7. Disaster Recovery & Business Continuity
        </h3>
        <p>
          RTO ≤ 60 mins. RPO ≤ 24 hrs. Daily encrypted backups (for
          client-managed DBs).
        </p>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">8. Client Responsibility</h3>
        <p>
          {clientName} serves as primary client, providing L1 support,
          coordinating access, and nominating business owners.
        </p>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">
          9. 25th Direct Responsibilities
        </h3>
        <p>
          Ensure platform availability, L3 support, system maintenance, security
          patches, DR processes, and integration support.
        </p>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">
          10. ANNEX B – Data Processing Addendum (DPA)
        </h3>
        <p>
          <strong>Roles:</strong> Controller ({clientName}), Processor (25th
          Direct).
        </p>
        <p>
          <strong>Scope:</strong> Processor acts only on written instructions,
          applies AES-256/TLS 1.2+, deletes data post-termination (~60 days).
        </p>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">
          11. Support Handbook (Annex C)
        </h3>
        <p>
          <strong>Support Tiers:</strong> Direct Start (Std), Direct Pro
          (Priority), Enterprise (Priority + QBR), VIP (24x7).
        </p>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">
          12. Implementation Timeline & Express Mode
        </h3>
        <p>
          <strong>Typical Timeline:</strong> 4 weeks for full deployment,
          express mode available (up to 30% faster).
        </p>
      </section>

      <section>
        <h3 className="font-bold text-lg mb-2">13. General Provisions</h3>
        <p>
          <strong>13.1 Force Majeure:</strong> Not liable for acts of God, war,
          etc. Notification within 48 hours.
        </p>
        <p>
          <strong>13.2 Third-Party Integrations:</strong> Not responsible for
          third-party interruptions or breaches.
        </p>
      </section> */}

      <div className="mt-10 border-t pt-8">
        <h3 className="font-bold text-xl mb-6">7. Signatures</h3>
        <p>
          IN WITNESS WHEREOF, the parties have caused this SLA to be executed by
          their duly authorized representatives as of the effective date.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-8">
          <div className="border p-6 rounded-lg bg-gray-50">
            <p className="font-bold mb-4">
              For: 25th & Staffing Limited (25th Suite)
            </p>
            <div className="space-y-3">
              <img src={img.atsuSign} alt="Atsu Sign" className="h-16" />
              <p className="border-b border-gray-400 pb-1">
                <strong>Name:</strong> Atsu Emmanuel
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Title:</strong> CTO
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>

          <div className="border p-6 rounded-lg bg-gray-50">
            <p className="font-bold mb-4">For: {clientName}</p>
            <div className="space-y-3">
              <div className="h-16 w-full">
                {readOnly ? (
                  <div className="h-16 flex items-end">
                    <span className="border-b border-gray-400 block w-48 text-gray-400 text-xs pb-1">
                      Signature
                    </span>
                  </div>
                ) : !signaturePreview ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Signature
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary file:text-white
                        hover:file:bg-primary/90
                        cursor-pointer"
                    />
                  </div>
                ) : (
                  <div
                    className="relative group cursor-pointer"
                    onClick={handleImageClick}
                  >
                    <img
                      src={signaturePreview}
                      alt="Uploaded Signature"
                      className="h-16 object-contain"
                    />
                    <button
                      onClick={handleRemoveClick}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 
                        opacity-0 group-hover:opacity-100 transition-opacity duration-200
                        hover:bg-red-600"
                      aria-label="Remove signature"
                    >
                      <X size={16} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
              <p className="border-b border-gray-400 pb-1">
                <strong>Name: </strong>
                {representativeName}
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Title: </strong>
                {formatText(representativeRole)}
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlaContent;
